// https://chatgpt.com/c/6930a1e6-b0e0-8331-ad05-51b8e6d5dc91
import React, { useEffect, useState } from "react";
import { clientAPI } from "@/lib/client-fetch";
import { trackAction } from "@/lib/track-action";
import API_ENDPOINTS from "@/constants/api-endpoints";
import CatalogItemSnippet from "@/components/common/catalog-item-snippet";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item"
import { Shapes, ChevronRightIcon, ArrowLeft } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Search } from "lucide-react";
import { useStoreLocation } from "@/context/store-location-context";

type SearchResult = any;

type Props = {
  searchStr: string;
};

export default function SearchResults({ searchStr }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const { storeLocation } = useStoreLocation();
  const [debouncedSearchStr, setDebouncedSearchStr] = useState(searchStr);
  const [items, setItems] = useState<SearchResult[]>([]);
  const [collections, setCollections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  //
  // 1. Debounce: wait 300ms after user stops typing
  //
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchStr(searchStr);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchStr]);

  //
  // 2. Fetch whenever debouncedSearchStr changes
  //
  useEffect(() => {
    if (!debouncedSearchStr.trim()) {
      setItems([]);
      setCollections([]);
      setError(null);
      return;
    }

    trackAction("SEARCH", debouncedSearchStr);

    let cancelled = false;

    async function run() {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({ search_string: debouncedSearchStr });
        if (storeLocation.city) params.set('city', storeLocation.city);
        const result = await clientAPI.get<{ catalog_items: any[]; collections: any[] }>(
          `${API_ENDPOINTS.PRODUCT_SEARCH}?${params}`
        );

        if (!result.ok) throw new Error(`Status ${result.status}`);

        if (!cancelled) {
          setItems(result.data?.catalog_items ?? []);
          setCollections(result.data?.collections ?? []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || "Unknown error");
          setItems([]);
          setCollections([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    return () => { cancelled = true; };
  }, [debouncedSearchStr, storeLocation.city]);
  
  const clearSearch = () => router.push(pathname);
  const preventDefaultAndClearSearch = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    clearSearch();
  };

  return (
    <div className="px-4 md:px-10 pt-6 pb-20">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={clearSearch}
          className="gap-1 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <h2 className="text-xl font-semibold text-foreground tracking-tight">Resultados: {searchStr}</h2>
      </div>
      {loading && (
        <div className="flex justify-center py-12">
          <Spinner className="size-8" />
        </div>
      )}
      {!loading && items.length === 0 && collections.length === 0 && debouncedSearchStr && (
        <Empty className="border border-dashed max-w-2xl my-8 mx-auto">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Search />
              </EmptyMedia>
              <EmptyTitle>{debouncedSearchStr}</EmptyTitle>
              <EmptyDescription>
                No se encontraron resultados para "{debouncedSearchStr}". Intenta <Link href="#" onClick={preventDefaultAndClearSearch} className="font-semibold underline cursor-pointer">restablecer los parámetros</Link> o <Link href="/soporte" className="font-semibold underline">contacta a servicio al cliente</Link> si tienes cualquier duda o pregunta.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <div className="flex gap-2">
                <Button className="cursor-pointer" variant="default" size="sm" onClick={clearSearch}>
                  Restablecer parámetros
                </Button>
                <Link href="/soporte">
                  <Button className="cursor-pointer"  variant="outline" size="sm">
                    Chat con soporte
                  </Button>
                </Link>
              </div>
            </EmptyContent>
        </Empty>
      )}
      {!loading && collections.length > 0 && (
        <div className="mt-6">
          {items.length > 0 && <h4 className="text-md font-semibold text-foreground tracking-tight mb-6 hidden md:block">Categorías</h4>}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
            {collections.map((collection: any, index: number) => (
              <Item key={collection.id} variant="outline" size="sm" asChild>
                <Link href={`/categorias/${collection.id}?prev=s`}>
                  <ItemMedia>
                    {!!collection.preview_image_url ? (
                      <Avatar className="size-8 rounded-lg">
                        <AvatarImage src={collection.preview_image_url} />
                        <AvatarFallback>{collection.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                    ) : <Shapes className="size-5" />}
                  </ItemMedia>
                  <ItemContent>
                    <ItemTitle><p className="line-clamp-1">{collection.name}</p></ItemTitle>
                  </ItemContent>
                  <ItemActions>
                    <ChevronRightIcon className="size-4" />
                  </ItemActions>
                </Link>
                </Item>
              ))
            }
          </div>
        </div>
      )}
      {!loading && items.length > 0 && (
        <div>
          {collections.length > 0 && <h4 className="text-md font-semibold text-foreground tracking-tight mt-8 mb-6 hidden md:block">Productos</h4 >}
          {collections.length > 0 && <div className="mb-2 md:hidden" />}
          {/* This duplicates code with CategoryContent.tsx */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8">
            {items.map((item: any) => (
              <CatalogItemSnippet
                key={item.id}
                item={item}
              />
            ))}    
          </div>
        </div>
      )}
    </div>    
  );
}
